import { useMarkPendingReviewAsSeen } from "@features/MarkPendingReviewAsSeen";
import { IBriefInflunecerPendingReviewFacets } from "@shared/lib/types/brief";
import { IBriefInfluencerSearchRequestV2 } from "@shared/lib/types/briefInfluencerV2";
import { Bell } from "lucide-react";
import {
  AgencyPendingReviewFlag,
  getAgencyPendingReviewChips,
} from "../lib/campaignBriefPendingReview";
import styles from "./CampaignBriefPendingReviewBar.module.scss";

type CampaignBriefPendingReviewBarProps = {
  facets?: IBriefInflunecerPendingReviewFacets;
  activeFlag: AgencyPendingReviewFlag | null;
  onFlagChange: (flag: AgencyPendingReviewFlag | null) => void;
  filterParams: Partial<IBriefInfluencerSearchRequestV2>;
};

export function CampaignBriefPendingReviewBar({
  facets,
  activeFlag,
  onFlagChange,
  filterParams,
}: CampaignBriefPendingReviewBarProps) {
  const { markAsSeen, isMarkAsSeenLoading } = useMarkPendingReviewAsSeen();
  const chips = getAgencyPendingReviewChips(facets);
  const changeCount = facets?.agency?.total ?? 0;
  const creatorCount = facets?.agency?.briefInfluencerCount ?? 0;
  const activeChip = chips.find((chip) => chip.id === activeFlag);

  if (chips.length === 0 && changeCount === 0) {
    return null;
  }

  const handleMarkAsSeen = () => {
    markAsSeen(
      {
        ...filterParams,
      },
      "brief"
    );
  };

  return (
    <div className={styles.bar}>
      <div className={styles.header}>
        <div className={styles.copy}>
          <div className={styles.iconWrap}>
            <Bell size={15} strokeWidth={2.25} />
            <span className={styles.iconDot} />
          </div>
          <div>
            <p className={styles.title}>New updates from the brand</p>
            <p className={styles.subtitle}>
              <span className={styles.emphasis}>{changeCount}</span> unread
              change{changeCount !== 1 ? "s" : ""} across{" "}
              <span className={styles.emphasis}>{creatorCount}</span> creator
              {creatorCount !== 1 ? "s" : ""}
              {activeChip
                ? ` · viewing “${activeChip.label}”`
                : " · tap a type to review"}
            </p>
          </div>
        </div>
        <div className={styles.actions}>
          {activeFlag ? (
            <button
              type="button"
              className={styles.showAllButton}
              onClick={() => onFlagChange(null)}
              disabled={isMarkAsSeenLoading}
            >
              Show all
            </button>
          ) : null}
          <button
            type="button"
            className={
              activeFlag ? styles.markAsSeenButton : styles.markAllSeenButton
            }
            onClick={handleMarkAsSeen}
            disabled={isMarkAsSeenLoading}
          >
            {isMarkAsSeenLoading
              ? "Marking…"
              : activeFlag
                ? "Mark as seen"
                : "Mark all seen"}
          </button>
        </div>
      </div>

      {chips.length > 0 ? (
        <div className={styles.chips}>
          {chips.map((chip) => {
            const isSelected = activeFlag === chip.id;
            return (
              <button
                key={chip.id}
                type="button"
                aria-pressed={isSelected}
                className={`${styles.chip} ${
                  isSelected ? styles.chipSelected : ""
                }`}
                onClick={() => onFlagChange(isSelected ? null : chip.id)}
              >
                <span className={styles.chipDot} />
                {chip.label}
                <span className={styles.chipCount}>{chip.count}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
